import React, { useState } from 'react';
import SectionHeader from '../components/SectionHeader';
import {
  btnPrimary,
  FERTILITY_DEMO_SECTION_BORDER,
  fertilityDemoEditButton,
  introCardKeyline,
  introHeadlineSerifDeep,
  demoChapterStackColumnStyle,
  demoSectionCardStackStyle,
} from '../designSystem';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const cardBase = {
  background: 'var(--white)',
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '16px 18px',
};

const savedTeamWrapper = {
  background: 'var(--white)',
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '16px 18px',
};

const savedDoctorCard = {
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 12,
  overflow: 'hidden',
  padding: 0,
  background: 'var(--card)',
};

const eyebrowPrivate = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--pink-d, #993556)',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  marginBottom: 6,
};

/** Yellow note surface — same for filled notes and empty / add-note state */
const PRIVATE_NOTE_BG = '#FFFDE7';
const PRIVATE_NOTE_BORDER = '#F9E57D';
const PRIVATE_NOTE_TEXT = '#4A3F00';
const privateNoteBoxBase = {
  background: PRIVATE_NOTE_BG,
  border: `0.5px solid ${PRIVATE_NOTE_BORDER}`,
  borderRadius: 8,
  padding: '9px 11px',
};

const eyebrowMutedUpper = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--muted, #7a6b63)',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  marginBottom: 6,
};

const pinkKeylineBottom = '1px solid rgba(212, 83, 126, 0.12)';

const JAIN_IMG = 'https://providerphotos.api.ohiohealth.com/api/image/get/1215017785';
const EMMA_IMG =
  'https://images.squarespace-cdn.com/content/v1/648f945aaaf1c8276ed8386c/38c6ff3d-0c6f-4137-93dd-2a378f204473/edit-2.jpg';

function hideImgShowFallback(e) {
  e.currentTarget.style.display = 'none';
  const n = e.currentTarget.nextElementSibling;
  if (n) n.style.display = 'flex';
}

/** 52px doctor avatar — img + initials fallback */
function DoctorPhoto52({ src, alt, initials, fallbackBg, fallbackColor, border }) {
  return (
    <>
      <img
        src={src}
        alt={alt}
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          objectFit: 'cover',
          objectPosition: 'center top',
          flexShrink: 0,
          border,
        }}
        onError={hideImgShowFallback}
      />
      <div
        style={{
          display: 'none',
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: fallbackBg,
          color: fallbackColor,
          fontSize: 13,
          fontWeight: 700,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border,
        }}
      >
        {initials}
      </div>
    </>
  );
}

const specialtyTabs = [
  { key: 'Acupuncture', label: 'Acupuncture', dashed: false },
  { key: 'Therapist', label: 'Therapist', dashed: false },
  { key: 'Nutritionist', label: 'Nutritionist', dashed: false },
  { key: 'Embryologist', label: 'Embryologist', dashed: false },
  { key: 'REI / Fertility', label: 'REI / Fertility', dashed: true },
];

const distanceOptions = ['10 mi', '25 mi', '50 mi'];

export default function FertilityDemoDoctors({ navigate, omitHeader }) {
  const [activeSpecialty, setActiveSpecialty] = useState('Acupuncture');
  const [activeDistance, setActiveDistance] = useState('10 mi');

  const mainContent = (
    <div style={demoSectionCardStackStyle}>
      {/* SECTION 1 — SAVED DOCTORS */}
      <div style={savedTeamWrapper}>
        <h2 className="section-title">Saved doctors</h2>
        <p className="section-descriptor" style={{ marginBottom: 16 }}>
          Add private notes about your experience and read what other women say about each provider.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* CARD 1 — Dr. Akas Jain */}
          <div style={savedDoctorCard}>
            {/* Section 1 — Doctor header */}
            <div
              style={{
                background: 'rgba(212, 83, 126, 0.04)',
                borderBottom: '1px solid rgba(212, 83, 126, 0.15)',
                padding: '14px 14px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div style={{ flexShrink: 0 }}>
                <DoctorPhoto52
                  src={JAIN_IMG}
                  alt="Dr. Akas Jain, MD"
                  initials="AJ"
                  fallbackBg="#fbeaf0"
                  fallbackColor="#993556"
                  border="2px solid var(--color-pink-light, #fbeaf0)"
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Dr. Akas Jain, MD</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: '#e1f5ee',
                      color: '#085041',
                    }}
                  >
                    ✓ Saved
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: '#fef0eb',
                      color: '#ad3f1a',
                    }}
                  >
                    Scan tomorrow
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Reproductive Endocrinology & Infertility</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Pinnacle Fertility · Westerville, OH</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: '#d4537e', fontSize: 11 }}>★★★★★</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>4.9</div>
                <div style={{ fontSize: 10, color: 'var(--muted)' }}>(23 reviews)</div>
              </div>
            </div>

            {/* Section 2 — My private note */}
            <div style={{ padding: '12px 14px', borderBottom: pinkKeylineBottom }}>
              <div style={eyebrowPrivate}>🔒 My private note</div>
              <div style={privateNoteBoxBase}>
                <div style={{ fontSize: 12, color: PRIVATE_NOTE_TEXT, fontStyle: 'italic', lineHeight: 1.5 }}>
                  Incredibly thorough. Remembered details from 3 months ago without checking my chart. Always makes time to answer questions.
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/signup')}
                  style={{ marginTop: 8, ...fertilityDemoEditButton }}
                >
                  Edit note
                </button>
              </div>
            </div>

            {/* Section 3 — Community reviews */}
            <div style={{ padding: '12px 14px', borderBottom: pinkKeylineBottom }}>
              <div style={eyebrowMutedUpper}>Community reviews (23)</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#fbeaf0',
                    color: '#993556',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  JM
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Jasmine M.</span>
                    <span style={{ color: '#d4537e', fontSize: 10 }}>★★★★★</span>
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>IVF cycle 2</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.45, marginTop: 2 }}>
                    Never felt rushed. Sat with me 45 min after retrieval. Remembered everything.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#fbeaf0',
                    color: '#993556',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  NR
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Noelle R.</span>
                    <span style={{ color: '#d4537e', fontSize: 10 }}>★★★★</span>
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>Egg freeze</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.45, marginTop: 2 }}>
                    Best REI in Columbus. Honest about timelines without being cold.
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 — Write a review */}
            <div style={{ padding: '10px 14px', borderBottom: pinkKeylineBottom }}>
              <div style={eyebrowMutedUpper}>Write a review</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <span key={i} style={{ color: '#ede8df', fontSize: 13, cursor: 'pointer' }} aria-hidden>
                    ★
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="Share your experience with Dr. Jain..."
                  style={{
                    flex: 1,
                    minWidth: 120,
                    border: '0.5px solid var(--border)',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 11,
                    fontFamily: FONT_BODY,
                  }}
                />
                <button
                  type="button"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 12px',
                    borderRadius: 8,
                    background: '#d4537e',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: FONT_BODY,
                  }}
                >
                  Post review
                </button>
              </div>
            </div>

            {/* Section 5 — Actions */}
            <div style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span onClick={() => navigate('/signup')} style={{ fontSize: 11, color: '#d4537e', fontWeight: 600, cursor: 'pointer' }}>
                All reviews →
              </span>
              <span onClick={() => navigate('/signup')} style={{ fontSize: 11, color: '#d4537e', fontWeight: 600, cursor: 'pointer' }}>
                Appointment prep →
              </span>
              <span onClick={() => navigate('/signup')} style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto', cursor: 'pointer' }}>
                Remove
              </span>
            </div>
          </div>

          {/* CARD 2 — Emma Cheung */}
          <div style={savedDoctorCard}>
            <div
              style={{
                background: 'rgba(212, 83, 126, 0.04)',
                borderBottom: '1px solid rgba(212, 83, 126, 0.15)',
                padding: '14px 14px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div style={{ flexShrink: 0 }}>
                <DoctorPhoto52
                  src={EMMA_IMG}
                  alt="Emma Cheung, L.Ac"
                  initials="EC"
                  fallbackBg="#eeedfe"
                  fallbackColor="#534ab7"
                  border="2px solid #eeedfe"
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Emma Cheung, L.Ac</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: '#e1f5ee',
                      color: '#085041',
                    }}
                  >
                    ✓ Saved
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Acupuncture · IVF Support & Fertility</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Acupuncture Specialists Ohio · Westerville, OH</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: '#d4537e', fontSize: 11 }}>★★★★★</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>5.0</div>
                <div style={{ fontSize: 10, color: 'var(--muted)' }}>(18 reviews)</div>
              </div>
            </div>

            <div style={{ padding: '12px 14px', borderBottom: pinkKeylineBottom }}>
              <div style={eyebrowPrivate}>🔒 My private note</div>
              <div
                style={{
                  ...privateNoteBoxBase,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, color: PRIVATE_NOTE_TEXT, fontStyle: 'italic', opacity: 0.72 }}>
                  No note yet — how has Emma been for you?
                </span>
                <span
                  onClick={() => navigate('/signup')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/signup')}
                  tabIndex={0}
                  role="button"
                  style={{ fontSize: 10, color: 'var(--pink)', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                >
                  + Add note
                </span>
              </div>
            </div>

            <div style={{ padding: '12px 14px', borderBottom: pinkKeylineBottom }}>
              <div style={eyebrowMutedUpper}>Community reviews (18)</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#eeedfe',
                    color: '#534ab7',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  SK
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Sarah K.</span>
                    <span style={{ color: '#d4537e', fontSize: 10 }}>★★★★★</span>
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>IVF support</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.45, marginTop: 2 }}>
                    Emma times sessions with my cycle perfectly. Noticeably calmer going into retrieval.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#eeedfe',
                    color: '#534ab7',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  AK
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Ava K.</span>
                    <span style={{ color: '#d4537e', fontSize: 10 }}>★★★★★</span>
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>PCOS · fertility</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.45, marginTop: 2 }}>
                    Helped me regulate my cycle for the first time in years. Highly recommend.
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '10px 14px', borderBottom: pinkKeylineBottom }}>
              <div style={eyebrowMutedUpper}>Write a review</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <span key={i} style={{ color: '#ede8df', fontSize: 13, cursor: 'pointer' }} aria-hidden>
                    ★
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="Share your experience with Emma..."
                  style={{
                    flex: 1,
                    minWidth: 120,
                    border: '0.5px solid var(--border)',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 11,
                    fontFamily: FONT_BODY,
                  }}
                />
                <button
                  type="button"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 12px',
                    borderRadius: 8,
                    background: '#d4537e',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: FONT_BODY,
                  }}
                >
                  Post review
                </button>
              </div>
            </div>

            <div style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span onClick={() => navigate('/signup')} style={{ fontSize: 11, color: '#d4537e', fontWeight: 600, cursor: 'pointer' }}>
                All reviews →
              </span>
              <span onClick={() => navigate('/signup')} style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto', cursor: 'pointer' }}>
                Remove
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2 — FIND A SPECIALIST */}
      <div style={cardBase}>
        <SectionHeader title="Find a specialist" descriptor="Search by name or clinic · filtered to your situation." />

        <div
          style={{
            background: 'var(--sand, #f9f6f1)',
            borderRadius: 8,
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 13 }} aria-hidden>
            💡
          </span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            You&apos;re mid-IVF with Dr. Jain — Nova is surfacing complementary care rather than new REIs right now. Looking for something else? Search below.
          </span>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {specialtyTabs.map((t) => {
            const active = activeSpecialty === t.key;
            if (t.dashed) {
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveSpecialty(t.key)}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '4px 12px',
                    borderRadius: 20,
                    border: '0.5px dashed var(--border)',
                    background: 'var(--white)',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    fontFamily: FONT_BODY,
                  }}
                >
                  {t.label}
                </button>
              );
            }
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveSpecialty(t.key)}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 12px',
                  borderRadius: 20,
                  border: active ? '0.5px solid #d4537e' : '0.5px solid var(--border)',
                  background: active ? '#d4537e' : 'var(--white)',
                  color: active ? 'white' : 'var(--muted)',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          <input
            type="search"
            placeholder="Search by name or clinic..."
            style={{
              flex: 1,
              border: '0.5px solid var(--border)',
              borderRadius: 8,
              padding: '9px 14px',
              fontSize: 13,
              background: 'var(--white)',
              fontFamily: FONT_BODY,
            }}
          />
          <button
            type="button"
            style={{
              background: '#d4537e',
              color: 'white',
              fontSize: 12,
              fontWeight: 600,
              padding: '9px 18px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              fontFamily: FONT_BODY,
            }}
          >
            Search
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {distanceOptions.map((d) => {
              const active = activeDistance === d;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setActiveDistance(d)}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: 20,
                    border: active ? '0.5px solid #d4537e' : '0.5px solid var(--border)',
                    background: active ? '#fbeaf0' : 'var(--white)',
                    color: active ? '#d4537e' : 'var(--muted)',
                    cursor: 'pointer',
                    fontFamily: FONT_BODY,
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>Top rated ↓</span>
        </div>

        {/* Regular results */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--sand, #ede8df)' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: '#ede8df',
                color: 'var(--muted)',
                fontSize: 11,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              MH
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Michael Hartlaub, L.Ac</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Thrive Acupuncture & Wellness · Powell · 12 miles</div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>★★★★ 4.7 · 8 OrivonAI reviews · Prenatal</div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              style={{
                border: '0.5px solid #f4c0d1',
                background: 'var(--white)',
                color: '#993556',
                fontSize: 10,
                fontWeight: 600,
                padding: '5px 10px',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                flexShrink: 0,
              }}
            >
              + Save
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: '#ede8df',
                color: 'var(--muted)',
                fontSize: 11,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              WQ
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Wei (Julia) Qin, L.Ac, MD (China)</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Medical Acupuncture & Herbs · Columbus · 5 miles</div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>★★★★ 4.6 · 5 OrivonAI reviews · Infertility</div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              style={{
                border: '0.5px solid #f4c0d1',
                background: 'var(--white)',
                color: '#993556',
                fontSize: 10,
                fontWeight: 600,
                padding: '5px 10px',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                flexShrink: 0,
              }}
            >
              + Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        fontFamily: FONT_BODY,
        ...demoChapterStackColumnStyle,
      }}
    >
      {!omitHeader && (
        <div
          style={{
            background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
            ...introCardKeyline,
            padding: '22px 26px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#993556', marginBottom: 3 }}>
              My Doctors · Fertility
            </div>
            <div style={{ ...introHeadlineSerifDeep, marginBottom: 2 }}>Your care team</div>
            <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)' }}>2 saved · Columbus, OH</div>
          </div>
          <button type="button" onClick={() => navigate('/signup')} style={{ ...btnPrimary, flexShrink: 0 }}>
            + Add doctor
          </button>
        </div>
      )}
      {mainContent}
    </div>
  );
}
